// Hierarchy Service
// Service for fetching business, functional, and geographic hierarchy data

import api from './api';

export interface BusinessHierarchy {
  id: string;
  name: string;
  level_type: 'WORLDWIDE' | 'BUSINESS_GROUP' | 'BUSINESS_ENTITY' | 'BUSINESS_LOCATION';
  parent_id?: string;
  level: number;
  status: 'ACTIVE' | 'INACTIVE';
}

export interface FunctionalHierarchy {
  id: string;
  name: string;
  level_type: 'FUNCTION' | 'SUB_FUNCTION' | 'FUNCTION_PROCESS';
  parent_id?: string;
  level: number;
  status: 'ACTIVE' | 'INACTIVE';
}

export interface GeographicHierarchy {
  id: string;
  name: string;
  level_type: 'WORLDWIDE' | 'CONTINENT' | 'COUNTRY' | 'STATE_REGION' | 'CITY';
  parent_id?: string;
  level: number;
  status: 'ACTIVE' | 'INACTIVE';
}

export interface HierarchyOption {
  value: string;
  label: string;
  level: number;
  parent_id?: string;
}

class HierarchyService {
  /**
   * Get business hierarchy options
   */
  async getBusinessHierarchy(): Promise<HierarchyOption[]> {
    try {
      const response = await api.get('/hierarchy/business');
      console.log('Business hierarchy API response:', {
        status: response.status,
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
        dataStructure: response.data
      });
      
      // Handle different response structures
      let hierarchies: BusinessHierarchy[] = [];
      if (response.data?.data && Array.isArray(response.data.data)) {
        hierarchies = response.data.data;
      } else if (Array.isArray(response.data)) {
        hierarchies = response.data;
      }
      
      console.log('Parsed hierarchies:', hierarchies.length);
      
      return hierarchies.map((item: BusinessHierarchy) => ({
        value: item.id,
        label: item.name, // Backend already adds indentation
        level: item.level,
        parent_id: item.parent_id
      }));
    } catch (error: any) {
      console.error('Error fetching business hierarchy:', {
        error,
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      return [];
    }
  }

  /**
   * Get functional hierarchy options
   */
  async getFunctionalHierarchy(): Promise<HierarchyOption[]> {
    try {
      const response = await api.get('/hierarchy/functional');
      console.log('Functional hierarchy API response:', {
        status: response.status,
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
        dataStructure: response.data
      });
      
      // Handle different response structures
      let hierarchies: FunctionalHierarchy[] = [];
      if (response.data?.data && Array.isArray(response.data.data)) {
        hierarchies = response.data.data;
      } else if (Array.isArray(response.data)) {
        hierarchies = response.data;
      }
      
      console.log('Parsed functional hierarchies:', hierarchies.length);
      
      return hierarchies.map((item: FunctionalHierarchy) => ({
        value: item.id,
        label: item.name, // Backend already adds indentation
        level: item.level,
        parent_id: item.parent_id
      }));
    } catch (error: any) {
      console.error('Error fetching functional hierarchy:', {
        error,
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      return [];
    }
  }

  /**
   * Get geographic hierarchy options
   */
  async getGeographicHierarchy(): Promise<HierarchyOption[]> {
    try {
      const response = await api.get('/hierarchy/geographic');
      console.log('Geographic hierarchy API response:', {
        status: response.status,
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
        dataStructure: response.data
      });
      
      // Handle different response structures
      let hierarchies: GeographicHierarchy[] = [];
      if (response.data?.data && Array.isArray(response.data.data)) {
        hierarchies = response.data.data;
      } else if (Array.isArray(response.data)) {
        hierarchies = response.data;
      }
      
      console.log('Parsed geographic hierarchies:', hierarchies.length);
      
      return hierarchies.map((item: GeographicHierarchy) => ({
        value: item.id,
        label: item.name, // Backend already adds indentation
        level: item.level,
        parent_id: item.parent_id
      }));
    } catch (error: any) {
      console.error('Error fetching geographic hierarchy:', {
        error,
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      return [];
    }
  }

  /**
   * Get business hierarchy options scoped to current user (roots where user sits and below).
   */
  async getBusinessHierarchyScoped(): Promise<HierarchyOption[]> {
    try {
      const response = await api.get('/hierarchy/business/scoped');
      const hierarchies: BusinessHierarchy[] = response.data?.data && Array.isArray(response.data.data)
        ? response.data.data
        : Array.isArray(response.data) ? response.data : [];
      return hierarchies.map((item: BusinessHierarchy) => ({
        value: item.id,
        label: item.name,
        level: item.level,
        parent_id: item.parent_id
      }));
    } catch (error: any) {
      console.error('Error fetching scoped business hierarchy:', error);
      return [];
    }
  }

  /**
   * Get functional hierarchy options scoped to current user (roots where user sits and below).
   */
  async getFunctionalHierarchyScoped(): Promise<HierarchyOption[]> {
    try {
      const response = await api.get('/hierarchy/functional/scoped');
      const hierarchies: FunctionalHierarchy[] = response.data?.data && Array.isArray(response.data.data)
        ? response.data.data
        : Array.isArray(response.data) ? response.data : [];
      return hierarchies.map((item: FunctionalHierarchy) => ({
        value: item.id,
        label: item.name,
        level: item.level,
        parent_id: item.parent_id
      }));
    } catch (error: any) {
      console.error('Error fetching scoped functional hierarchy:', error);
      return [];
    }
  }

  /**
   * Get geographic hierarchy options scoped to current user (roots where user sits and below).
   */
  async getGeographicHierarchyScoped(): Promise<HierarchyOption[]> {
    try {
      const response = await api.get('/hierarchy/geographic/scoped');
      const hierarchies: GeographicHierarchy[] = response.data?.data && Array.isArray(response.data.data)
        ? response.data.data
        : Array.isArray(response.data) ? response.data : [];
      return hierarchies.map((item: GeographicHierarchy) => ({
        value: item.id,
        label: item.name,
        level: item.level,
        parent_id: item.parent_id
      }));
    } catch (error: any) {
      console.error('Error fetching scoped geographic hierarchy:', error);
      return [];
    }
  }

  /**
   * Get functional hierarchy level-1 (root) nodes only.
   * Returns top-level function names; API returns flat list with level 0 = root.
   */
  async getFunctionalLevel1(): Promise<HierarchyOption[]> {
    try {
      const all = await this.getFunctionalHierarchyScoped();
      return all.filter((item) => item.level === 0);
    } catch (error) {
      console.error('Error fetching functional level 1:', error);
      return [];
    }
  }

  /**
   * Get functional hierarchy level-2 nodes only – for use as Area labels (e.g. Process Admin).
   * Returns second-level function names; API returns flat list with level 1 = second level.
   */
  async getFunctionalLevel2(): Promise<HierarchyOption[]> {
    try {
      const all = await this.getFunctionalHierarchyScoped();
      return all.filter((item) => item.level === 1);
    } catch (error) {
      console.error('Error fetching functional level 2:', error);
      return [];
    }
  }

  /**
   * Get subprocess options for a specific function
   */
  async getSubprocesses(functionId: string): Promise<HierarchyOption[]> {
    try {
      const response = await api.get(`/api/hierarchy/functional/${functionId}/subprocesses`);
      return response.data.map((item: FunctionalHierarchy) => ({
        value: item.id,
        label: item.name,
        level: item.level,
        parent_id: item.parent_id
      }));
    } catch (error) {
      console.error('Error fetching subprocesses:', error);
      return [];
    }
  }
}

export const hierarchyService = new HierarchyService();
